/**
 * Error Mapper - Converts technical errors to user-friendly messages
 *
 * Ensures end users never see raw API errors, technical messages, or stack traces.
 * All technical details are logged in debug mode but sanitized for user display.
 */

import type { PaymentError } from '../../types';

/**
 * Map error codes to user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Validation errors
  VALIDATION_ERROR: 'Please complete all card fields.',
  invalid_card_number: 'Please check your card number and try again.',
  INVALID_CARD_NUMBER: 'Please check your card number and try again.',
  invalid_expiry: 'Please check the expiration date and try again.',
  INVALID_EXPIRY: 'Please check the expiration date and try again.',
  invalid_cvc: 'Please check the security code and try again.',
  INVALID_CVC: 'Please check the security code and try again.',

  // Card errors
  card_declined: 'Your card was declined. Please try another payment method or contact your bank.',
  CARD_DECLINED: 'Your card was declined. Please try another payment method or contact your bank.',
  insufficient_funds: 'Your card has insufficient funds. Please try another payment method.',
  INSUFFICIENT_FUNDS: 'Your card has insufficient funds. Please try another payment method.',
  expired_card: 'This card has expired. Please use a different card.',
  EXPIRED_CARD: 'This card has expired. Please use a different card.',
  incorrect_cvc: 'The security code is incorrect. Please check and try again.',
  INCORRECT_CVC: 'The security code is incorrect. Please check and try again.',
  processing_error: 'Your card could not be processed. Please try another card.',
  PROCESSING_ERROR: 'Your card could not be processed. Please try another card.',

  // 3DS errors
  THREE_DS_FAILED: '3D Secure authentication failed. Please try again.',
  THREE_DS_CANCELED: 'Authentication was canceled. Please try again to complete your payment.',
  three_ds_not_supported: "This card doesn't support secure authentication. Please try a different card.",
  THREE_DS_NOT_SUPPORTED: "This card doesn't support secure authentication. Please try a different card.",

  // Network errors
  NETWORK_ERROR: 'Connection error. Please check your internet connection and try again.',
  NETWORK_TIMEOUT: 'Connection timeout. Please try again.',
  network_error: 'Connection error. Please check your internet connection and try again.',
  timeout: 'Connection timeout. Please try again.',

  // System errors
  PAYMENT_FAILED: 'Payment could not be processed. Please try again.',
  PAYMENT_NOT_FOUND: 'Payment session has expired. Please start a new payment.',
  PAYMENT_ALREADY_PROCESSED: 'This payment has already been processed successfully.',
  AUTHENTICATION_ERROR: 'Authentication failed. Please refresh the page and try again.',
  STATUS_CHECK_FAILED: 'Could not verify payment status. Please contact support.',
  POLLING_ERROR: 'Could not verify payment status. Please try again.',
  PAYMENT_PROCESSING: 'Your payment is being processed. Please wait a moment and check your payment status.',

  // Tokenization errors
  tokenization_failed: 'Card information could not be processed. Please check your card details and try again.',
  TOKENIZATION_FAILED: 'Card information could not be processed. Please check your card details and try again.',

  // Stripe-specific codes
  requires_payment_method: 'Your card was declined. Please try another payment method.',
  test_mode_live_card: 'This card cannot be used in test mode. Please use a test card number.',

  // Generic Stripe error codes
  generic_decline: 'Your card was declined. Please try another payment method or contact your bank.',
  lost_card: 'This card has been reported lost. Please use a different card.',
  stolen_card: 'This card has been reported stolen. Please use a different card.',
  do_not_honor: 'Your card was declined. Please contact your bank for more information.',
  do_not_try_again: 'Your card was declined. Please use a different payment method.',
};

/**
 * Default fallback message for unknown errors
 */
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again or contact support if the problem persists.';

/**
 * Convert API error response to user-friendly PaymentError
 */
export function mapApiError(
  response: any,
  statusCode: number,
  debugMode: boolean = false
): PaymentError {
  // Log raw error for debugging
  if (debugMode) {
    console.log('[ErrorMapper] Raw API error:', {
      statusCode,
      response,
    });
  }

  // Handle case where error details are nested in a message field as JSON string
  // Example: { message: "Status code: 424\nBody: {...}" }
  if (response?.message && typeof response.message === 'string') {
    // Try to extract JSON from the message field
    const jsonMatch = response.message.match(/Body:\s*(\{[\s\S]*\})/);
    if (jsonMatch) {
      try {
        const parsedBody = JSON.parse(jsonMatch[1]);
        if (debugMode) {
          console.log('[ErrorMapper] Extracted nested JSON from message field:', parsedBody);
        }
        // Recursively call mapApiError with the extracted body
        return mapApiError(parsedBody, statusCode, debugMode);
      } catch (e) {
        // Failed to parse nested JSON, continue with normal flow
        if (debugMode) {
          console.log('[ErrorMapper] Failed to parse nested JSON:', e);
        }
      }
    }
  }

  // Handle structured error responses (like your 3DS 424 example)
  if (response?.error) {
    const apiError = response.error;

    // Extract meaningful user message from structured error
    let userMessage = DEFAULT_ERROR_MESSAGE;
    let errorCode = 'UNKNOWN_ERROR';

    // Check for 3DS-specific errors
    if (apiError.errorSource === '3DS Server' || response.title?.includes('3DS')) {
      errorCode = 'THREE_DS_NOT_SUPPORTED';

      // Use the details array if available (like your example)
      if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
        userMessage = apiError.details[0];
      } else if (apiError.message) {
        // Map known 3DS messages
        if (apiError.message.toLowerCase().includes('not supported')) {
          userMessage = ERROR_MESSAGES.THREE_DS_NOT_SUPPORTED || DEFAULT_ERROR_MESSAGE;
        } else {
          userMessage = apiError.message;
        }
      }
    }
    // Check for other structured error patterns
    else if (apiError.code) {
      errorCode = apiError.code;
      const mappedMessage = ERROR_MESSAGES[errorCode];
      userMessage = mappedMessage || (apiError.message ? apiError.message : DEFAULT_ERROR_MESSAGE);
    } else if (apiError.message) {
      // Try to map the message to a known error
      const normalizedMessage = apiError.message.toLowerCase();

      if (normalizedMessage.includes('declined')) {
        errorCode = 'CARD_DECLINED';
        userMessage = ERROR_MESSAGES.CARD_DECLINED || DEFAULT_ERROR_MESSAGE;
      } else if (normalizedMessage.includes('insufficient')) {
        errorCode = 'INSUFFICIENT_FUNDS';
        userMessage = ERROR_MESSAGES.INSUFFICIENT_FUNDS || DEFAULT_ERROR_MESSAGE;
      } else if (normalizedMessage.includes('expired')) {
        errorCode = 'EXPIRED_CARD';
        userMessage = ERROR_MESSAGES.EXPIRED_CARD || DEFAULT_ERROR_MESSAGE;
      } else if (normalizedMessage.includes('3ds') || normalizedMessage.includes('3d secure')) {
        errorCode = 'THREE_DS_FAILED';
        userMessage = ERROR_MESSAGES.THREE_DS_FAILED || DEFAULT_ERROR_MESSAGE;
      } else {
        // Use the API message if it seems user-friendly (no technical jargon)
        if (!hasTechnicalJargon(apiError.message)) {
          userMessage = apiError.message;
        }
      }
    }

    // Determine if error is retryable
    const retryable = isRetryableError(errorCode, statusCode);

    return {
      code: errorCode,
      message: sanitizeMessage(userMessage),
      retryable,
      statusCode,
      details: debugMode ? response : undefined,
    };
  }

  // Handle simple error messages
  if (response?.message) {
    const errorCode = response.code || 'PAYMENT_FAILED';
    const mappedMessage = ERROR_MESSAGES[errorCode];
    const userMessage = mappedMessage ||
      (hasTechnicalJargon(response.message) ? DEFAULT_ERROR_MESSAGE : response.message);

    return {
      code: errorCode,
      message: sanitizeMessage(userMessage),
      retryable: isRetryableError(errorCode, statusCode),
      statusCode,
      details: debugMode ? response : undefined,
    };
  }

  // Handle HTTP errors without structured response
  let errorCode = 'PAYMENT_FAILED';
  let userMessage = DEFAULT_ERROR_MESSAGE;

  if (statusCode === 404) {
    errorCode = 'PAYMENT_NOT_FOUND';
    userMessage = ERROR_MESSAGES.PAYMENT_NOT_FOUND || DEFAULT_ERROR_MESSAGE;
  } else if (statusCode === 401 || statusCode === 403) {
    errorCode = 'AUTHENTICATION_ERROR';
    userMessage = ERROR_MESSAGES.AUTHENTICATION_ERROR || DEFAULT_ERROR_MESSAGE;
  } else if (statusCode === 424) {
    // 424 Failed Dependency - often used for 3DS errors
    errorCode = 'THREE_DS_FAILED';
    userMessage = ERROR_MESSAGES.THREE_DS_FAILED || DEFAULT_ERROR_MESSAGE;
  } else if (statusCode >= 500) {
    userMessage = 'Our payment system is temporarily unavailable. Please try again in a few moments.';
  }

  return {
    code: errorCode,
    message: sanitizeMessage(userMessage),
    retryable: isRetryableError(errorCode, statusCode),
    statusCode,
    details: debugMode ? response : undefined,
  };
}

/**
 * Map error codes to user-friendly messages
 */
export function mapErrorCode(code: string, originalMessage?: string): string {
  // Check if we have a mapped message
  if (ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }

  // If original message exists and seems user-friendly, use it
  if (originalMessage && !hasTechnicalJargon(originalMessage)) {
    return sanitizeMessage(originalMessage);
  }

  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Map generic exceptions to PaymentError
 */
export function mapException(error: unknown, debugMode: boolean = false): PaymentError {
  if (debugMode) {
    console.log('[ErrorMapper] Exception:', error);
  }

  // Network timeout
  if (error instanceof Error && error.name === 'AbortError') {
    return {
      code: 'NETWORK_TIMEOUT',
      message: ERROR_MESSAGES.NETWORK_TIMEOUT || DEFAULT_ERROR_MESSAGE,
      retryable: true,
    };
  }

  // Network error
  if (error instanceof TypeError) {
    return {
      code: 'NETWORK_ERROR',
      message: ERROR_MESSAGES.NETWORK_ERROR || DEFAULT_ERROR_MESSAGE,
      retryable: true,
    };
  }

  // Error with message
  if (error instanceof Error) {
    // Check if error message contains technical details
    if (hasTechnicalJargon(error.message)) {
      return {
        code: 'PAYMENT_FAILED',
        message: DEFAULT_ERROR_MESSAGE,
        retryable: true,
        details: debugMode ? { originalError: error.message } : undefined,
      };
    }

    return {
      code: 'PAYMENT_FAILED',
      message: sanitizeMessage(error.message),
      retryable: true,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: DEFAULT_ERROR_MESSAGE,
    retryable: true,
  };
}

/**
 * Check if error code or status indicates a retryable error
 */
function isRetryableError(errorCode: string, statusCode?: number): boolean {
  // Network errors are always retryable
  if (errorCode.includes('NETWORK') || errorCode.includes('TIMEOUT')) {
    return true;
  }

  // Validation errors are retryable (user can fix input)
  if (errorCode.includes('INVALID') || errorCode.includes('VALIDATION')) {
    return true;
  }

  // Card errors are often retryable (try different card)
  const retryableCardErrors = [
    'CARD_DECLINED',
    'INSUFFICIENT_FUNDS',
    'EXPIRED_CARD',
    'INCORRECT_CVC',
    'THREE_DS_FAILED',
    'THREE_DS_CANCELED',
    'THREE_DS_NOT_SUPPORTED',
  ];

  if (retryableCardErrors.includes(errorCode)) {
    return true;
  }

  // Some HTTP statuses indicate retryable errors
  if (statusCode && (statusCode === 408 || statusCode === 429 || statusCode >= 500)) {
    return true;
  }

  // Default to not retryable for unknown errors
  return false;
}

/**
 * Check if message contains technical jargon that shouldn't be shown to users
 */
function hasTechnicalJargon(message: string): boolean {
  const technicalTerms = [
    'proxy',
    'json',
    'html',
    'parse',
    'undefined',
    'null',
    'exception',
    'stack trace',
    'basis theory',
    'api',
    'endpoint',
    'tokenintent',
    'fetch',
    'response.status',
    'http',
    'ssl',
    'tls',
  ];

  const lowerMessage = message.toLowerCase();
  return technicalTerms.some(term => lowerMessage.includes(term));
}

/**
 * Sanitize message to ensure it's user-friendly
 */
function sanitizeMessage(message: string): string {
  // Remove technical prefixes
  let sanitized = message
    .replace(/^Error:\s*/i, '')
    .replace(/^[A-Z_]+:\s*/, '') // Remove ERROR_CODE: prefix
    .trim();

  // Ensure first letter is capitalized and ends with period
  if (sanitized.length > 0) {
    sanitized = sanitized.charAt(0).toUpperCase() + sanitized.slice(1);
    if (!sanitized.endsWith('.')) {
      sanitized += '.';
    }
  }

  return sanitized;
}